import React, { Component } from 'react';
import { connect } from 'react-redux';
import Timeline from './Timeline/Timeline';
import SceneTop from './SceneTop/SceneTop';
import SceneBottom from './SceneBottom/SceneBottom';
import SceneUI from './SceneUI/SceneUI';
import * as Mover from '../../../../utils/Mover';
import * as Features from '../../../../utils/Features';
import * as SoundManager from '../../../../utils/SoundManager';
import * as utils from '../../../../utils';
import LineCursor from './LineCursor/LineCursor';
import Intro from './Intro/Intro';
import Outro from './Outro/Outro';
import { SUB_SECTION } from '../../../../constants';
import { TIMELINE } from '../../../../content';
import { SPECIAL_BULLETS } from '../../../../texts';
import { setHoveredSpot, soundBlocked } from '../../../../redux/actions';
import './Scene.css';

class Scene extends Component {
  constructor(props) {
    super(props);
    this.state = {
      screenX: 0,
      posX: 0,
      timeLabel: '', // time string or text label
    };

    // ref
    this.sceneMoveRef = React.createRef();

    // bind
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.loop = this.loop.bind(this);
  }

  // -------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------

  componentWillMount() {
    this.props.soundBlocked(SoundManager.muteAtmo(false));
  }

  componentDidMount() {
    this.startLoop();
  }

  componentWillUnmount() {
    SoundManager.muteAtmo(true);
    this.stopLoop();
  }

  // -------------------------------------------------------
  // Mouse move
  // -------------------------------------------------------

  getCalcTimeLabel(posX) {
    let time = '';
    for (let i = TIMELINE.length - 3; i >= 1; i -= 1) {
      if (posX >= TIMELINE[i].pos) {
        const posPc =
          (posX - TIMELINE[i].pos) / (TIMELINE[i + 1].pos - TIMELINE[i].pos);
        const minutes = TIMELINE[i + 1].time - TIMELINE[i].time;
        time = utils.minutesToTimeString(
          TIMELINE[i].time + Math.round(minutes * posPc)
        );
        break;
      }
    }
    return time;
  }

  getEdgeTimeLabel(posX) {
    if (
      posX >= TIMELINE[0].posLimits.min &&
      posX <= TIMELINE[0].posLimits.max
    ) {
      return SPECIAL_BULLETS.START;
    } else if (
      posX >= TIMELINE[TIMELINE.length - 1].posLimits.min &&
      posX <= TIMELINE[TIMELINE.length - 1].posLimits.max
    ) {
      return SPECIAL_BULLETS.END;
    } else if (
      posX > TIMELINE[1].posLimits.min &&
      posX <= TIMELINE[1].posLimits.max
    ) {
      return utils.minutesToTimeString(TIMELINE[1].time);
    } else if (
      posX >= TIMELINE[TIMELINE.length - 2].posLimits.min &&
      posX < TIMELINE[TIMELINE.length - 2].posLimits.max
    ) {
      return utils.minutesToTimeString(TIMELINE[TIMELINE.length - 2].time);
    }
    return '';
  }

  getHoverSpot(posX) {
    let spotId = -1;
    for (let i = 0; i < TIMELINE.length; i += 1) {
      if (
        posX > TIMELINE[i].posLimits.min &&
        posX < TIMELINE[i].posLimits.max
      ) {
        spotId = i;
        break;
      }
    }
    return spotId;
  }

  moveUpdate(pageX, pageY) {
    const { width, hoveredSpot } = this.props;

    Mover.updateMousePosition(pageY);
    SoundManager.atmoUpdate();

    // x cursor position
    const posX = Math.min(Math.max(pageX / width, 0), 1);

    let timeLabel = '';
    if (posX > TIMELINE[1].pos && posX < TIMELINE[TIMELINE.length - 2].pos) {
      // calc time
      timeLabel = this.getCalcTimeLabel(posX);
    } else {
      // edge cases
      timeLabel = this.getEdgeTimeLabel(posX);
    }

    // try hover
    // note - maybe merge with cursor position check - there are probably few same lines
    const hoverId = this.getHoverSpot(posX);
    if (hoverId !== hoveredSpot) {
      this.props.setHoveredSpot(hoverId);
    }

    // update state
    this.setState({
      screenX: pageX,
      posX,
      timeLabel,
    });
  }

  handleTouchMove(e) {
    this.moveUpdate(e.touches[0].clientX, e.touches[0].clientY);
  }

  handleMouseMove(e) {
    this.moveUpdate(e.pageX, e.pageY);
  }

  // -------------------------------------------------------
  // Loop
  // -------------------------------------------------------

  loop() {
    Mover.loop();
    this.sceneMoveRef.current.style.transform = `translateY(${Mover.getCenterOffsetY()}px)`;
    this.loopRef = window.requestAnimationFrame(this.loop);
  }

  startLoop() {
    if (!Features.features.mobile && !this.loopRef) {
      this.loopRef = window.requestAnimationFrame(this.loop);
    }
  }

  stopLoop() {
    window.cancelAnimationFrame(this.loopRef);
  }

  // -------------------------------------------------------
  // Render
  // -------------------------------------------------------

  render() {
    const {
      subSection,
      subPage,
      sceneVideo,
      sceneVideoPosition,
      width,
      height,
    } = this.props;
    const { screenX, posX, timeLabel } = this.state;

    // set scene style
    const subActive = subPage.length > 0;
    const sceneStyle = {
      position: subActive ? 'absolute' : 'relative',
      height: `${height}px`,
      width: `${width}px`,
    };
    const sceneMoveStyle = {
      height: `${height}px`,
      width: `${width}px`,
    };

    if (Features.features.mobile) {
      sceneMoveStyle.top =
        sceneVideo.length > 0 ? `${height * 0.5 * sceneVideoPosition}px` : 0;
    }

    return (
      <div
        id="scene"
        style={sceneStyle}
        onMouseMove={this.handleMouseMove}
        onTouchMove={this.handleTouchMove}
      >
        <div id="scene-move" style={sceneMoveStyle} ref={this.sceneMoveRef}>
          <SceneTop posX={posX} />
          <SceneBottom posX={posX} />
          <Timeline />
        </div>
        <LineCursor screenX={screenX} timeLabel={timeLabel} />
        {subSection === SUB_SECTION.OUTRO && <Outro />}
        {subSection === SUB_SECTION.INTRO && <Intro />}
        <SceneUI />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  subSection: state.app.subSection,
  subPage: state.app.subPage,
  hoveredSpot: state.app.hoveredSpot,
  sceneVideo: state.app.sceneVideo,
  sceneVideoPosition: state.app.sceneVideoPosition,
  width: state.ui.width,
  height: state.ui.height,
});

const mapDispatchToProps = dispatch => ({
  setHoveredSpot: spotId => dispatch(setHoveredSpot(spotId)),
  soundBlocked: soundBlockState => dispatch(soundBlocked(soundBlockState)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Scene);



// WEBPACK FOOTER //
// ./src/components/App/Dukla/Scene/Scene.js